import {
    HttpClient,
    HttpParams
} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {
    Observable,
} from 'rxjs';
import {
    map
} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {CaseType} from '../../classes/CaseType';
import {Dictionary} from '../../classes/Dictionary';
import {MatterType} from '../../classes/MatterType';
import {
    Case as CaseEa,
    CreateCaseRequest,
    ExistingCasePopup
} from '../../classes/rest-classes/Case';
import {CreateCaseResponse} from '../../classes/rest-classes/CreateCaseResponse';
import {EaCase} from '../../classes/rest-classes/EstateAdmin';
import {BaseRestService} from './baseRestService';
import {ProductSummary} from '../../classes/rest-classes/ProductSummary';
import {Case} from "../../classes/Case";

@Injectable({
    providedIn: 'root',
})

export class RestCaseService extends BaseRestService {
    private readonly BASE_URL: string;
    private readonly MESSAGE_URL: string;

    constructor(private http: HttpClient) {
        super();
        this.BASE_URL = environment.restApiUrl + '/Case';
        this.MESSAGE_URL = environment.notificationApiUrl;
    }

    public getCaseEa(caseid: number): Observable<CaseEa> {
        if (caseid) {
            const url = `${this.BASE_URL}/${caseid}`;
            let queryParams = new HttpParams();
            return this.http.get<CaseEa>(url, {
                params: queryParams,
                headers: this.authHeaders,
            }).pipe(map((response) => ({
                    ...response,
                    lines: response.lines.map((line) => ({
                        ...line,
                        lineDetails: line.lineDetails.filter((detail) => detail?.status !== 23),
                    })).filter(line => line.lineDetails && line.lineDetails.length > 0),
                }))
            );
        }
    }

    public getListOfCases(caseId: number): Observable<any> {
        let queryParams = new HttpParams();
        queryParams = queryParams.append('CaseFileId', caseId);
        return this.http.get<any>(this.BASE_URL, {params: queryParams, headers: this.authHeaders});
    }


    public getCorrectCase<T>(caseid: number): Observable<T> {
        const url = `${this.BASE_URL}/${caseid}`;
        return this.http.get<T>(url, {headers: this.authHeaders});
    }

    public getMessage(): Observable<any> {
        const url = `${this.MESSAGE_URL}`;
        return this.http.get(url, {headers: this.authHeaders});
    }

    public getProductSummaryByCaseId(caseId: number): Observable<ProductSummary> {
        const url = `${this.BASE_URL}/${caseId}/ProductSummary`;
        return this.http.get<ProductSummary>(url, {headers: this.authHeaders});
    }

    public createCase(request: CreateCaseRequest): Observable<CreateCaseResponse> {
        return this.http.post<CreateCaseResponse>(this.BASE_URL, request, {
            headers: this.authHeaders,
        });
    }

    public updateCase(caseFile: CaseEa, eaCase: EaCase): Observable<any> {
        const parsedCaseFile = JSON.parse(JSON.stringify(caseFile));
        const parsedEaCase = JSON.parse(JSON.stringify(eaCase));
        parsedCaseFile.eaCase = parsedEaCase;
        const request = {Casefile: parsedCaseFile, EaCase: parsedEaCase};
        return this.http.put(this.BASE_URL, request, {headers: this.authHeaders});
    }

    public getMatterTypesAsDictionary(productType: number | null): Observable<Dictionary[]> {
        return this.getCaseMatterTypes(productType).pipe(
            map((resp) =>
                resp.map((item) => {
                    return <Dictionary>{
                        key: item.matterTypeId,
                        value: item.matterTypeDescription,
                    };
                })
            )
        );
    }

    public getMatterTypes(productType: number | null): Observable<MatterType[]> {
        return this.getCaseMatterTypes(productType).pipe(
            map((resp) =>
                resp.map((item) => {
                    return <MatterType>{
                        id: item.matterTypeId,
                        description: item.matterTypeDescription,
                    };
                })
            )
        );
    }

    public getCaseTypeMatterTypes(productType: number | null): Observable<Dictionary[]> {
        return this.getCaseMatterTypes(productType).pipe(
            map((resp) =>
                resp.map((item) => {
                    // NOTE: Ugly-hack - in this case "Dictionary.value" is used like "number"
                    return <Dictionary>{
                        key: item.caseTypeId,
                        value: <string>(<unknown>item.matterTypeId),
                    };
                })
            )
        );
    }

    public getCaseType(productType: number): Observable<number> {
        return this.getCaseMatterTypes(productType).pipe(
            map((resp) => resp?.[0]?.caseTypeId || 0)
        );
    }

    public getCaseTypes(): Observable<CaseType[]> {
        const url = `${this.BASE_URL}/Types`;
        return this.http.get<CaseType[]>(url, {headers: this.authHeaders});
    }

    private getCaseMatterTypes(productType: number | null): Observable<CaseTypeMatterType[]> {
        const url = `${this.BASE_URL}/MatterTypes`;
        let params = new HttpParams();
        if (productType != null) {
            params = params.append('productType', productType);
        }

        return this.http.get<CaseTypeMatterType[]>(url, {params, headers: this.authHeaders});
    }

    public getLiCase(caseId: number): Observable<any> {
        const url = `${this.BASE_URL}/${caseId}`;
        let queryParams = new HttpParams();
        return this.http.get<any>(url, {
            params: queryParams,
            headers: this.authHeaders,
        }).pipe(map((response) => ({
                ...response,
                lines: response.lines.map((line) => ({
                    ...line,
                    lineDetails: line.lineDetails.filter(
                        (detail) => detail?.status !== 23),
                })).filter(line => line.lineDetails && line.lineDetails.length > 0),
            }))
        );
    }

    public duplicateClientRefCheck(AccountId: string | number, caseReference: string, accountUserId: number): Observable<any> {
        let queryParams = new HttpParams();
        queryParams = queryParams.append('AccountId', AccountId);
        queryParams = queryParams.append('caseReference', caseReference);
        queryParams = queryParams.append('accountUserId', accountUserId);
        return this.http.get<any>(this.BASE_URL, {params: queryParams, headers: this.authHeaders});
    }

    public getExistingCasesAddress(accountId: string, postcode: string, buildingName: string, buildingNumber: string, subBuildingName: string): Observable<ExistingCasePopup[]> {
        const url = `${this.BASE_URL}/ByAddress`;
        let queryParams = new HttpParams()
            .set('AccountId', accountId)
            .set('Postcode', postcode)
            .set('BuildingName', buildingName ? buildingName : buildingNumber)
            .set('BuildingNumber', buildingNumber ? buildingNumber : buildingName)
            .set('SubBuildingName', subBuildingName ? subBuildingName : '')
        return this.http.get<ExistingCasePopup[]>(url, {params: queryParams, headers: this.authHeaders});
    }

    public getEaCaseWithDetails(caseId: number): Observable<Case> {
        const url = `${this.BASE_URL}/${caseId}`;
        let queryParams = new HttpParams();
        queryParams = queryParams.append('caseId', caseId);
        return this.http.get<Case>(url, {
            params: queryParams,
            headers: this.authHeaders,
        }).pipe(map((response) => ({
                ...response,
                lines: response.lines.map((line) => ({
                    ...line,
                    lineDetails: line.lineDetails.filter(
                        (detail) => detail?.status !== 23),
                })).filter(line => line.lineDetails && line.lineDetails.length > 0),
            }))
        );
    }
}

class CaseTypeMatterType {
    caseTypeId: number;
    matterTypeDescription: string;
    matterTypeId: number;
    productType: number;
}
